
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, CreditCard, PlusCircle, MinusCircle, Search, Download, Filter } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface SuperAdminWallet {
  id: string;
  superAdminId: string;
  superAdminName: string;
  domain: string;
  balance: number;
  lastTransaction?: string;
  lastTransactionDate?: Date;
}

interface WalletTransferDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onTransfer: (toWalletId: string, amount: number) => void;
  wallets: SuperAdminWallet[];
  masterBalance: number;
}

const WalletTransferDialog: React.FC<WalletTransferDialogProps> = ({ 
  isOpen, onClose, onTransfer, wallets, masterBalance 
}) => {
  const [selectedWalletId, setSelectedWalletId] = useState<string>('');
  const [amount, setAmount] = useState<number>(100);

  const handleTransfer = () => {
    if (selectedWalletId && amount > 0 && amount <= masterBalance) {
      onTransfer(selectedWalletId, amount);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transfer Funds to Super Admin Wallet</DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="wallet-select">Select Super Admin Wallet</label>
            <select 
              id="wallet-select"
              className="w-full p-2 border rounded-md"
              value={selectedWalletId}
              onChange={(e) => setSelectedWalletId(e.target.value)}
            >
              <option value="">Select a wallet</option>
              {wallets.map(wallet => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.superAdminName} ({wallet.domain})
                </option>
              ))}
            </select>
          </div>
          
          <div className="grid gap-2">
            <label htmlFor="transfer-amount">Amount to Transfer</label>
            <Input 
              id="transfer-amount"
              type="number"
              min={1}
              max={masterBalance}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
            <p className="text-sm text-muted-foreground">
              Maximum available: ${masterBalance.toLocaleString()}
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleTransfer}
            disabled={!selectedWalletId || amount <= 0 || amount > masterBalance}
          >
            Transfer Funds
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MasterWalletManager: React.FC = () => {
  const { toast } = useToast();
  const [wallets, setWallets] = useState<SuperAdminWallet[]>([]);
  const [masterBalance, setMasterBalance] = useState(10000); // Starting with mock balance
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    setIsLoading(true);
    try {
      // In a production app, this would be an API call to get real wallet data
      // For now, use mock data to demonstrate the functionality
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const mockWallets: SuperAdminWallet[] = [
        {
          id: 'w1',
          superAdminId: 'sa-001',
          superAdminName: 'John Smith',
          domain: 'techsolutions.referme.site',
          balance: 2500,
          lastTransaction: 'Order fulfillment',
          lastTransactionDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        },
        {
          id: 'w2',
          superAdminId: 'sa-002',
          superAdminName: 'Jane Doe',
          domain: 'marketing.referme.site',
          balance: 1500,
          lastTransaction: 'Funds transfer',
          lastTransactionDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
        },
        {
          id: 'w3',
          superAdminId: 'sa-003',
          superAdminName: 'Michael Johnson',
          domain: 'healthcare.referme.site',
          balance: 3750,
          lastTransaction: 'Order fulfillment',
          lastTransactionDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
        },
        {
          id: 'w4',
          superAdminId: 'sa-004',
          superAdminName: 'Sarah Williams',
          domain: 'education.referme.site',
          balance: 950,
          lastTransaction: 'Manual adjustment',
          lastTransactionDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)
        }
      ];
      
      setWallets(mockWallets);
      
      // Calculate master balance (sum of all wallets + master's own balance)
      const totalSuperAdminBalance = mockWallets.reduce((total, wallet) => total + wallet.balance, 0);
      setMasterBalance(10000 + totalSuperAdminBalance);
    } catch (error) {
      console.error('Error fetching wallets:', error);
      toast({
        title: "Error",
        description: "Failed to load wallet data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdjustBalance = (walletId: string, amount: number) => {
    // In a real implementation, this would call an API to adjust the balance
    // For demo purposes, we'll just update the state
    setWallets(prevWallets => 
      prevWallets.map(wallet => {
        if (wallet.id === walletId) {
          return {
            ...wallet,
            balance: wallet.balance + amount,
            lastTransaction: amount > 0 ? 'Manual credit' : 'Manual debit',
            lastTransactionDate: new Date()
          };
        }
        return wallet;
      })
    );
    
    // Also update master balance
    setMasterBalance(prev => prev + amount);
    
    toast({
      title: "Balance Adjusted",
      description: `${amount > 0 ? 'Added' : 'Subtracted'} $${Math.abs(amount)} from wallet balance`,
      variant: amount > 0 ? "default" : "destructive"
    });
  };

  const handleTransferFunds = (toWalletId: string, amount: number) => {
    // Find the wallet to transfer to
    const toWallet = wallets.find(wallet => wallet.id === toWalletId);
    if (!toWallet) return;

    // Update the wallet balance
    handleAdjustBalance(toWalletId, amount);
    
    // Update master balance (already handled by handleAdjustBalance)
    
    toast({
      title: "Funds Transferred",
      description: `$${amount.toLocaleString()} transferred to ${toWallet.superAdminName}'s wallet`,
      variant: "default"
    });
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const filteredWallets = wallets.filter(wallet => {
    const searchLower = searchQuery.toLowerCase();
    return (
      wallet.superAdminName.toLowerCase().includes(searchLower) ||
      wallet.domain.toLowerCase().includes(searchLower)
    );
  });

  const exportToCSV = () => {
    // Create CSV content
    const headers = ['Super Admin', 'Domain', 'Balance', 'Last Transaction', 'Date'];
    const rows = wallets.map(wallet => [
      wallet.superAdminName,
      wallet.domain,
      `$${wallet.balance.toLocaleString()}`,
      wallet.lastTransaction || 'N/A',
      formatDate(wallet.lastTransactionDate)
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `wallet-balances-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Master Wallet Manager</CardTitle>
            <CardDescription>
              Manage balances for all super admin instances
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Master Balance</div>
            <div className="text-2xl font-bold">${masterBalance.toLocaleString()}</div>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => setIsTransferDialogOpen(true)}
            >
              <PlusCircle className="h-4 w-4 mr-1" /> Transfer Funds
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <div className="relative w-72">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or domain..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={fetchWallets} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Super Admin</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Last Transaction</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWallets.length > 0 ? (
                  filteredWallets.map(wallet => (
                    <TableRow key={wallet.id}>
                      <TableCell className="font-medium">
                        {wallet.domain}
                      </TableCell>
                      <TableCell>{wallet.superAdminName}</TableCell>
                      <TableCell className="font-medium">
                        <Badge variant={wallet.balance < 500 ? "destructive" : "default"}>
                          ${wallet.balance.toLocaleString()}
                        </Badge>
                      </TableCell>
                      <TableCell>{wallet.lastTransaction || 'N/A'}</TableCell>
                      <TableCell>{formatDate(wallet.lastTransactionDate)}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdjustBalance(wallet.id, 100)}
                        >
                          <PlusCircle className="h-4 w-4 mr-1" /> Add
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdjustBalance(wallet.id, -100)}
                        >
                          <MinusCircle className="h-4 w-4 mr-1" /> Deduct
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-4">
                      No wallets found matching your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        
        <div className="flex justify-end mt-4">
          <Button>
            <PlusCircle className="h-4 w-4 mr-2" />
            Add New Wallet
          </Button>
        </div>

        <WalletTransferDialog
          isOpen={isTransferDialogOpen}
          onClose={() => setIsTransferDialogOpen(false)}
          onTransfer={handleTransferFunds}
          wallets={wallets}
          masterBalance={masterBalance}
        />
      </CardContent>
    </Card>
  );
};

export default MasterWalletManager;
